import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { authApi, inr, fmtDate } from '@/lib/erpApi';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, FileSignature } from 'lucide-react';

const STATUSES = ['Active', 'Expired', 'Terminated'];

const blank = {
  tenant_id: '', unit_id: '', monthly_rent: 0, security_deposit: 0, rent_due_day: 5,
  agreement_start: '', agreement_end: '', possession_date: '', move_in_date: '', move_out_date: '',
  agreement_doc: '', status: 'Active', notes: '',
};

export default function AgreementsPage() {
  const { token } = useAuth();
  const api = authApi(token);
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, t, u, b] = await Promise.all([
        api.get('/erp/agreements'),
        api.get('/erp/tenants'),
        api.get('/erp/units'),
        api.get('/erp/buildings'),
      ]);
      setRows(a.data); setTenants(t.data); setUnits(u.data); setBuildings(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const tenantMap = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants]);
  const unitMap = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);
  const buildingMap = useMemo(() => Object.fromEntries(buildings.map((b) => [b.id, b.name])), [buildings]);

  const openNew = () => {
    if (!tenants.length || !units.length) return toast.error('Create at least one tenant and one unit first');
    setEditing(null); setForm({ ...blank, tenant_id: tenants[0].id, unit_id: units[0].id, monthly_rent: units[0].monthly_rent || 0, security_deposit: units[0].security_deposit || 0 }); setOpen(true);
  };
  const openEdit = (a) => { setEditing(a); setForm({ ...blank, ...a }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.tenant_id || !form.unit_id || !form.agreement_start) return toast.error('Tenant, unit and start date required');
    setSaving(true);
    try {
      const payload = { ...form };
      ['monthly_rent', 'security_deposit', 'rent_due_day'].forEach((k) => { payload[k] = Number(payload[k] || 0); });
      ['agreement_end', 'possession_date', 'move_in_date', 'move_out_date'].forEach((k) => { if (!payload[k]) delete payload[k]; });
      if (editing) {
        await api.put(`/erp/agreements/${editing.id}`, payload);
        toast.success('Agreement updated');
      } else {
        await api.post('/erp/agreements', payload);
        toast.success('Agreement created — unit flipped to Occupied');
      }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm('Delete this agreement? Unit will be freed if it was Active.')) return;
    try {
      await api.delete(`/erp/agreements/${a.id}`);
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="erp-agreements-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Agreements</h1>
          <p className="text-gray-500 text-sm mt-1">Bind a tenant to a unit. Creating an Active agreement auto-marks the unit Occupied.</p>
        </div>
        <Button onClick={openNew} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-agreement-btn">
          <Plus className="h-4 w-4 mr-2" /> New Agreement
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-gray-500">No agreements yet.</CardContent></Card>
      ) : (
        <Card><CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="p-3">Tenant</th>
                <th className="p-3">Unit</th>
                <th className="p-3">Rent</th>
                <th className="p-3">Deposit</th>
                <th className="p-3">Start · End</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const t = tenantMap[a.tenant_id]; const u = unitMap[a.unit_id];
                return (
                  <tr key={a.id} className="border-t hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-900 flex items-center gap-2"><FileSignature className="h-4 w-4 text-brand-orange" /> {t?.name || '—'}</td>
                    <td className="p-3 text-gray-700">{u ? `${buildingMap[u.building_id] || ''} / ${u.unit_number}` : '—'}</td>
                    <td className="p-3 font-mono">{inr(a.monthly_rent)}</td>
                    <td className="p-3 font-mono">{inr(a.security_deposit)}</td>
                    <td className="p-3 text-gray-600 text-xs">{fmtDate(a.agreement_start)} → {fmtDate(a.agreement_end)}</td>
                    <td className="p-3"><Badge variant={a.status === 'Active' ? 'default' : 'outline'} className={a.status === 'Active' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : ''}>{a.status}</Badge></td>
                    <td className="p-3 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(a)}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(a)}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Agreement' : 'New Agreement'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1 md:col-span-2">
                <Label>Tenant *</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.mobile}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Unit *</Label>
                <Select value={form.unit_id} onValueChange={(v) => {
                  const u = unitMap[v];
                  setForm({ ...form, unit_id: v, monthly_rent: u?.monthly_rent || 0, security_deposit: u?.security_deposit || 0 });
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{buildingMap[u.building_id] || ''} / {u.unit_number} · {u.category} · {u.status}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Monthly Rent (₹) *</Label><Input type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} required /></div>
              <div className="space-y-1"><Label>Security Deposit (₹)</Label><Input type="number" value={form.security_deposit} onChange={(e) => setForm({ ...form, security_deposit: e.target.value })} /></div>
              <div className="space-y-1"><Label>Rent Due Day (1–31)</Label><Input type="number" min={1} max={31} value={form.rent_due_day} onChange={(e) => setForm({ ...form, rent_due_day: e.target.value })} /></div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Agreement Start *</Label><Input type="date" value={form.agreement_start} onChange={(e) => setForm({ ...form, agreement_start: e.target.value })} required /></div>
              <div className="space-y-1"><Label>Agreement End</Label><Input type="date" value={form.agreement_end || ''} onChange={(e) => setForm({ ...form, agreement_end: e.target.value })} /></div>
              <div className="space-y-1"><Label>Possession Date</Label><Input type="date" value={form.possession_date || ''} onChange={(e) => setForm({ ...form, possession_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Move-in Date</Label><Input type="date" value={form.move_in_date || ''} onChange={(e) => setForm({ ...form, move_in_date: e.target.value })} /></div>
              <div className="space-y-1 md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
